export type StatementStatus = "pending" | "processing" | "completed" | "failed";

export const DOC_TYPES = [
  "bank_statement",
  "application",
  "voided_check",
  "drivers_license",
  "ssn_card",
  "articles_of_incorporation",
  "filing_receipt",
  "tax_return",
  "zero_balance_letter",
  "ar_report",
  "irs_tin_letter",
  "mca_contract",
  "mca_disclosure",
  "mca_addendum",
  "unknown",
] as const;
export type DocType = (typeof DOC_TYPES)[number];

/**
 * The doc-type select's value for a requested "Other" document's own name
 * (`TrackedFileDocument.custom_label`): not a type, never sent as one.
 */
export const CUSTOM_LABEL_OPTION = "custom_label";
export type DocTypeChoice = DocType | typeof CUSTOM_LABEL_OPTION;

export const DOC_TYPE_LABELS: Record<DocType, string> = {
  bank_statement: "Bank Statement",
  application: "Application Form",
  voided_check: "Voided Check",
  drivers_license: "Driver's License",
  ssn_card: "SSN Card",
  articles_of_incorporation: "Articles of Incorporation",
  filing_receipt: "Filing Receipts for Entity",
  tax_return: "Tax Return",
  zero_balance_letter: "Zero Balance Letter",
  ar_report: "A/R Report",
  irs_tin_letter: "IRS TIN Letter",
  mca_contract: "MCA Contract",
  mca_disclosure: "MCA Disclosure",
  mca_addendum: "MCA Addendum",
  unknown: "Other",
};

/** Section headers in Application Documents (groups related DocTypes). */
export const DOC_SECTIONS: ReadonlyArray<{
  key: string;
  label: string;
  types: readonly DocType[];
}> = [
  {
    key: "contract",
    label: "Contract",
    types: ["mca_contract", "mca_disclosure", "mca_addendum"],
  },
  { key: "bank_statement", label: "Bank Statement", types: ["bank_statement"] },
  { key: "application", label: "Application Form", types: ["application"] },
  {
    key: "identification",
    label: "Identification",
    types: [
      "drivers_license",
      "ssn_card",
      "voided_check",
      "articles_of_incorporation",
      "filing_receipt",
      "irs_tin_letter",
    ],
  },
  {
    key: "other",
    label: "Other",
    types: ["tax_return", "zero_balance_letter", "ar_report", "unknown"],
  },
] as const;

export type FileStatus = "processing" | "processed" | "extraction_failed" | "failed";

export interface TrackedFileDocument {
  id: number;
  filename: string;
  file_url?: string | null;
  source_type?: string | null;
  doc_type?: DocType | null;
  file_status?: FileStatus | null;
  size_bytes: number;
  bank_statement_id?: number | null;
  bank_statement_status?: StatementStatus | null;
  created_at: string;
  /** The name staff gave a requested "Other" document; only while it is still Other. */
  custom_label?: string;
}

export interface BankStatement {
  id: number;
  borrower_id: number;
  filename: string;
  file_url?: string;
  file_size: number;
  status: StatementStatus;
  created_at: string;
  updated_at: string;
}

export interface StatementAnalysis {
  id: number;
  bank_statement_id: number;
  summary: StatementSummary;
  processing_status: string;
  created_at: string;
  updated_at: string;
}

export interface StatementSummary {
  bank_name?: string;
  account_number?: string;
  account_type?: string;
  start_date?: string;
  end_date?: string;
  start_balance?: number;
  end_balance?: number;
  total_credits?: number;
  total_debits?: number;
  num_transactions?: number;
  company?: string;
  reconciled?: boolean;
}

export interface StatementsState {
  statements: BankStatement[];
  currentStatement: BankStatement | null;
  loading: boolean;
  error: string | null;
  processingStatements: number[];
}
